import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queryKeys'
import type { ChatMessage, ChatReply, Setup, Stage } from '@/shared/api/types'

export const setupApi = {
  state: (signal?: AbortSignal) => api.get<Setup>('/setup', signal),
  chat: (signal?: AbortSignal) => api.get<ChatMessage[]>('/setup/chat', signal),
  send: (message: string) => api.post<ChatReply>('/setup/chat', { message }),
  reset: () => api.delete<void>('/setup/chat'),

  addOpener: (text: string) => api.post<Setup>('/setup/openers', { text }),
  updateOpener: (id: number, text: string) => api.put<Setup>(`/setup/openers/${id}`, { text }),
  deleteOpener: (id: number) => api.delete<Setup>(`/setup/openers/${id}`),
  reorderOpeners: (ids: number[]) => api.put<Setup>('/setup/openers/order', { ids }),

  addStage: (stage: Omit<Stage, 'id'>) => api.post<Setup>('/setup/stages', stage),
  updateStage: (id: number, stage: Omit<Stage, 'id'>) => api.put<Setup>(`/setup/stages/${id}`, stage),
  deleteStage: (id: number) => api.delete<Setup>(`/setup/stages/${id}`),
  reorderStages: (ids: number[]) => api.put<Setup>('/setup/stages/order', { ids }),
}

export function useSetup() {
  return useQuery({
    queryKey: queryKeys.setup,
    queryFn: ({ signal }) => setupApi.state(signal),
  })
}

export function useSetupChat() {
  return useQuery({
    queryKey: queryKeys.setupChat,
    queryFn: ({ signal }) => setupApi.chat(signal),
  })
}

/**
 * Ход разговора с настройщиком.
 *
 * Ответ приносит и реплику, и новое состояние проекта — одним запросом.
 * Иначе после каждого хода пришлось бы отдельно перезапрашивать карту
 * шагов, и она обновлялась бы с задержкой на глазах у человека.
 *
 * Реплика человека появляется в ленте сразу, не дожидаясь модели: пока
 * та думает секунду, отправленное сообщение должно быть на экране —
 * иначе кажется, что нажатие не сработало, и его жмут второй раз.
 */
export function useSendMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (message: string) => setupApi.send(message),
    onMutate: (message: string) => {
      const text = message.trim()
      if (!text) return

      queryClient.setQueryData<ChatMessage[]>(queryKeys.setupChat, (prev) => [
        ...(prev ?? []),
        {
          // Отрицательный номер: настоящий придёт с сервера, а этот
          // нужен только как ключ списка и ни с чем не столкнётся.
          id: -Date.now(),
          role: 'user',
          text,
          at: new Date().toISOString(),
        },
      ])
    },
    onError: (_error, message) => {
      // Ход не прошёл — убираем реплику из ленты. Оставить её значит
      // показать разговор, которого не было: модель этих слов не
      // слышала и в истории на сервере их нет.
      const text = message.trim()
      queryClient.setQueryData<ChatMessage[]>(queryKeys.setupChat, (prev) =>
        (prev ?? []).filter((item) => !(item.id < 0 && item.text === text)),
      )
    },
    onSuccess: (reply) => {
      queryClient.setQueryData(queryKeys.setup, reply.setup)
      void queryClient.invalidateQueries({ queryKey: queryKeys.setupChat })
      // Промпт собирается из этих же данных — его предпросмотр устарел.
      void queryClient.invalidateQueries({ queryKey: queryKeys.promptPreview })
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile })
      void queryClient.invalidateQueries({ queryKey: queryKeys.account })
    },
  })
}

/** Правка списков руками. Ответ — то же состояние, что и у чата. */
export function useSetupMutation<T>(fn: (input: T) => Promise<Setup>) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: fn,
    onSuccess: (setup) => {
      queryClient.setQueryData(queryKeys.setup, setup)
      void queryClient.invalidateQueries({ queryKey: queryKeys.promptPreview })
    },
  })
}

/**
 * Флаг «настроить позже».
 *
 * Пока проект не готов, кабинет отправляет в онбординг. Но запирать
 * человека там нельзя: возможно, он пришёл посмотреть на продукт, а не
 * заполнять анкету. Флаг в localStorage — осознанное «потом», и после
 * него кабинет открывается как обычно.
 */
const SKIP_KEY = 'sellora:onboarding-skipped'

export function onboardingSkipped(): boolean {
  return localStorage.getItem(SKIP_KEY) === '1'
}

export function markOnboardingSkipped() {
  localStorage.setItem(SKIP_KEY, '1')
}

/** Сбрасывается при регистрации: «потом» прошлого аккаунта не должно
 * открывать кабинет новому. */
export function clearOnboardingSkipped() {
  localStorage.removeItem(SKIP_KEY)
}
